import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import '../style.css';
import { Breadcrumbs } from '../../../components/Atom/breadcumbs';
import { ArrowLeftOutlined, MailOutlined, ScheduleOutlined, PhoneOutlined, UserOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ShortenId from 'components/Atom/ShortenId';
import StatusTag from 'components/Atom/StatusTag';
import { Button, Button as ANTButton, Empty, Form, Modal, Radio, Skeleton, Input, Checkbox, notification } from 'antd';
import { useSelector } from 'react-redux';
import { getSingleInvoice, sendInvoicePaymentLink, markPaidSingleInvoice, emailInvoicePdf, getInvoiceAttachmentSignedUrl, getInvoiceAttachmentDownloadSignedUrl } from 'api/subscriptions';
import { useQuery } from 'react-query';
import { capitalizeWords } from 'utils/helper';
import { checkUserPermissions, formatAmount } from 'utils/services';
import { USER_ROLES, TIMEZONES } from 'utils/constant';
import PricingModelBreakdown from './assets/PricingModelBreakdown';
import LoadingSkeleton from '../../../components/Skeleton/index';
import { ResetButton } from '../../../components/Atom/Button';
import { formatInTimeZone } from 'date-fns-tz';

export const ViewInvoiceDetails = () => {
  const [form] = Form?.useForm();
  const [isInvoicePaid, setIsInvoicePaid] = useState(false);
  const [open, setOpen] = useState(false);
  const [openNote, setOpenNote] = useState(false);
  const [mode, setMode] = useState('invoice_customer');
  const [manualPaidNote, setManualPaidNote] = useState(null);
  const [type, setType] = useState(null);
  const [isPaymentLinkSending, setIsPaymentLinkSending] = useState(false);
  const [isInvoicePdfSending, setIsInvoicePdfSending] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [isAttachmentDownloading, setIsAttachmentDownloading] = useState(false);
  const hiddenFileInput = useRef(null);
  const location = useLocation();
  const { id } = useParams();
  const store = useSelector((state) => state.company.store);
  const navigate = useNavigate();
  // const [record, setRecord] = useState(location?.state?.record);
  const fetchInvoiceData = async () => {
    const response = await getSingleInvoice(location?.state?.record?.store_id || store?._id, id || location?.state?.id);
    return response?.data?.data || null;
  };
  const {
    data: record,
    refetch,
    isLoading,
    isFetching,
  } = useQuery(`invoices_${id || location?.state?.record?.invoice_id}`, fetchInvoiceData, {
    enabled: false,
    manual: true,
  });
  // useEffect(() => {
  //   setRecord(location?.state?.record);
  // }, [store]);
  useEffect(() => {
    refetch();
  }, [store]);

  const handleRadioChange = (e) => {
    setMode(e.target.value);
  };

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleOpenNoteModal = () => {
    setOpenNote(!openNote);
  };

  const handleAttachmentClick = () => hiddenFileInput?.current?.click();

  const handleAttachmentFileChange = (e) => {
    const file = e?.target?.files[0];
    if (file) setAttachmentFile(file);
    e.target.value = '';
  };

  const handleAttachmentRemove = () => {
    setAttachmentFile(null);
    if (hiddenFileInput?.current) hiddenFileInput.current.value = '';
  };

  const handleAttachmentDownload = async () => {
    if (isAttachmentDownloading) return;
    const invoiceId = id || location?.state?.record?.invoice_id;
    setIsAttachmentDownloading(true);

    if (attachmentFile) {
      const blobUrl = URL.createObjectURL(attachmentFile);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachmentFile?.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } else if (record?.attachment) {
      const response = await getInvoiceAttachmentDownloadSignedUrl(record?.store_id, invoiceId);
      const validUrl = response?.data?.data;
      if (validUrl) {
        window.open(validUrl?.url, '_blank', 'noopener,noreferrer');
      } else {
        notification.error({ message: 'Failed to get download link.', placement: 'bottomRight' });
      }
    }

    setIsAttachmentDownloading(false);
  };

  const handleInvoicePaid = async () => {
    setIsInvoicePaid(true);
    try {
      let attachmentFileName = null;

      if (attachmentFile) {
        const signedUrlResponse = await getInvoiceAttachmentSignedUrl(record?.store_id, attachmentFile?.name);
        if (!signedUrlResponse?.data?.data) {
          notification.error({ message: 'Failed to upload attachment. Please try again.', placement: 'bottomRight' });
          setIsInvoicePaid(false);
          return;
        }
        const uploadResponse = await fetch(signedUrlResponse?.data?.data, {
          method: 'PUT',
          headers: { 'Content-Type': attachmentFile?.type },
          body: attachmentFile,
        });
        if (!uploadResponse?.ok) {
          notification.error({ message: 'Failed to upload attachment. Please try again.', placement: 'bottomRight' });
          setIsInvoicePaid(false);
          return;
        }
        attachmentFileName = attachmentFile?.name;
      }

      const payload = {
        ...(manualPaidNote && { note: manualPaidNote }),
        ...(attachmentFileName && { attachment: attachmentFileName }),
      };
      const response = await markPaidSingleInvoice(record?.store_id, id || location?.state?.record?.invoice_id, payload);
      if (response?.data?.success) refetch();
    } catch (err) {
      notification.error({ message: 'Something went wrong. Please try again.', placement: 'bottomRight' });
    } finally {
      setIsInvoicePaid(false);
      setAttachmentFile(null);
      handleOpenNoteModal();
    }
  };

  const initialValues = {
    email: '',
    name: '',
    phone: '',
  };

  const markPaidInvoiceInitialValues = {
    update_subscription: false,
  };

  const handleSubmit = async (values) => {
    if (type === 'payment_link') {
      handleSendPaymentLink(values);
    } else if (type === 'invoice_pdf') {
      handleEmailInvoicePdf(values);
    }
  };

  const handleSendPaymentLink = async (values) => {
    setIsPaymentLinkSending(true);

    const payload = {
      invoice_id: id || location?.state?.record?.invoice_id,
      mode: mode,
      ...(mode === 'customer' && {
        customer: {
          email: values?.email,
          name: values?.name,
          phone: values?.phone,
        },
      }),
    };
    const response = await sendInvoicePaymentLink(record?.store_id, payload);
    if (response?.data?.success) {
      refetch();
    }
    setIsPaymentLinkSending(false);
    handleOpen();
    setType(null);
  };

  const handleEmailInvoicePdf = async (values) => {
    setIsInvoicePdfSending(true);

    const payload = {
      invoice_id: id || location?.state?.record?.invoice_id,
      mode: mode,
      ...(mode === 'customer' && {
        customer: {
          email: values?.email,
          name: values?.name,
          phone: values?.phone,
        },
      }),
    };
    const response = await emailInvoicePdf(record?.store_id, payload);
    if (response?.data?.success) {
      refetch();
    }
    setIsInvoicePdfSending(false);
    handleOpen();
    setType(null);
  };

  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'Invoice',
        href: '/subscriptions/invoice',
        data: { id: location?.state?.id, isRedirection: true, filtersData: location?.state?.filtersData, searchValue: location?.state?.searchValue },
      },
      {
        name: <ShortenId value={id || location?.state?.id || 'Not Found'} />,
      },
    ],
  };
  const time_zone = store?.settings?.timezone || TIMEZONES.PKT;
  const generalInformation = [
    { label: 'Invoice ID', value: <ShortenId value={record?.invoice_id} /> },
    { label: 'Plan ID', value: <ShortenId value={record?.plan_id} /> },
    { label: 'Subscription ID', value: <ShortenId value={record?.subscription_id?.subscription_id || record?.subscription_id} /> },
    { label: 'Status', value: record?.state ? <StatusTag tag={record?.state} /> : <StatusTag tag={'pending'} /> },
    { label: 'Last Update Time', value: record?.updatedAt ? formatInTimeZone(record?.updatedAt, time_zone, 'MMM d, yyyy h:mm:ss a') : 'N/A' },
    ...(record?.combination_operator ? [{ label: 'Combination Operator', value: record?.combination_operator || 'N/A' }] : []),
    ...(record?.or_strategy ? [{ label: 'Whichever Strategy', value: record?.or_strategy || 'N/A' }] : []),
    ...(record?.is_csv_based ? [{ label: 'CSV Based', value: record?.is_csv_based ? 'Yes' : 'No' }] : []),
    ...(record?.csv_amount ? [{ label: 'CSV Amount', value: record?.csv_amount || 'N/A' }] : []),
    ...(record?.components?.[0]?.base_amount ? [{ label: 'Base Amount', value: record?.components?.[0]?.base_amount?.toLocaleString('en-US', { maximumSignificantDigits: 21 }) || 'N/A' }] : []),
  ];

  const paymentDetails = [
    ...(record?.state === 'paid' && record?.is_manually_paid
      ? [{ label: 'Manually Paid', value: record?.is_manually_paid ? 'Yes' : 'No' }]
      : [{ label: 'Payment Token', value: <ShortenId value={record?.subscription?.payment?.token || record?.payment?.token || 'N/A'} /> }]),
    ...(record?.pricing_model_breakdown && record?.pricing_model_breakdown?.length > 0 && !record?.subscription?.payment?.token && !record?.payment?.token
      ? [{ label: 'Tokenless', value: 'Yes' }]
      : []),
    { label: 'Total Amount', value: formatAmount(record?.amount || 0, record?.currency) || '--' },
    { label: 'Currency', value: record?.currency || 'N/A' },
    { label: 'Payment Date', value: record?.payment_date ? formatInTimeZone(record?.payment_date, time_zone, 'MMM d, yyyy h:mm:ss a') : 'N/A' },
  ];

  return (
    <>
      <div className='header-container p-4 custom-sticky'>
        <div className='payment-breadcrums'>
          <div
            className='back-button'
            onClick={() =>
              navigate('/subscriptions/invoice', {
                state: {
                  id: location?.state?.id,
                  isRedirection: true,
                  filtersData: location?.state?.filtersData,
                  searchValue: location?.state?.searchValue,
                },
              })
            }>
            <ArrowLeftOutlined /> <span style={{ fontWeight: 600 }}>Back</span>
          </div>
          <span style={{ color: '#8C8C8C' }}>|</span>
          <Breadcrumbs {...breadcrumbProps} />
          <ResetButton onClick={refetch} loading={isFetching} />
        </div>
        <div className='detail-heading mt-4'>Invoice Detail</div>
      </div>
      <div className='subscription-details logs-details'>
        {isLoading ? (
          <LoadingSkeleton />
        ) : !record ? (
          store?.isSelected ? (
            <div className='empty-custom-style'>
              <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            <div className='empty-custom-style'>
              <Empty description={'Select store from the drop-down menu above'} />
            </div>
          )
        ) : (
          <>
            <div className='details-section '>
              <div className='heading-section'>
                <h3>Invoice Detail</h3>
              </div>
              <div className='detail-card'>
                {generalInformation?.map((info, index) => (
                  <div key={index} className='detail-value'>
                    <h2>{info?.label}</h2> <span>{info?.value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className='details-section'>
              <div className='heading-section'>
                <h3>Payment Details</h3>
              </div>
              <div className='detail-card'>
                {paymentDetails?.map((info, index) => (
                  <div key={index} className='detail-value'>
                    <h2>{info?.label}</h2> <span>{info?.value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className='details-section'>
              <div className='heading-section'>
                <h3>Customer Details</h3>
              </div>
              <div className='detail-card'>
                <div className='detail-value'>
                  <h2>Name</h2> <span>{record?.subscription?.customer?.name || record?.customer?.name || 'N/A'}</span>
                </div>
                <div className='detail-value'>
                  <h2>Email</h2> <span>{record?.subscription?.customer?.email || record?.customer?.email || 'N/A'}</span>
                </div>
                <div className='detail-value'>
                  <h2>Phone</h2> <span>{record?.subscription?.customer?.phone || record?.customer?.phone || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Invoice manually marked as Paid */}
            {(record?.note || record?.attachment) && (
              <div className='details-section'>
                <div className='heading-section'>
                  <h3>Notes</h3>
                </div>
                <div className='detail-card'>
                  {record?.note && (
                    <div className='detail-value'>
                      <h2>Custom Note</h2> <span>{record?.note}</span>
                    </div>
                  )}
                  {record?.attachment && (
                    <div className='detail-value'>
                      <h2>Attachment</h2>
                      <span
                        role='button'
                        tabIndex={0}
                        onClick={isAttachmentDownloading ? undefined : handleAttachmentDownload}
                        onKeyDown={(e) => {
                          if (!isAttachmentDownloading && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            handleAttachmentDownload();
                          }
                        }}
                        style={{
                          color: '#1677ff',
                          textDecoration: 'underline',
                          cursor: isAttachmentDownloading ? 'default' : 'pointer',
                        }}>
                        {isAttachmentDownloading ? 'Loading...' : record?.attachment}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {record?.is_csv_based && record?.csvBasedAdditionalCharges && record?.csvBasedAdditionalCharges?.length > 0 && (
              <div className='details-section'>
                <div className='heading-section'>
                  <h3>Additional Charges</h3>
                </div>
                <div className='detail-card'>
                  {record?.csvBasedAdditionalCharges?.map((charge) => (
                    <Fragment key={charge?.key}>
                      <div className='detail-value'>
                        <span>{capitalizeWords(charge?.key)}</span>
                      </div>
                      <div className='detail-value'>
                        <span>
                          {charge?.value || 'N/A'}
                          {charge?.type === 'percentage' ? '%' : ''}
                        </span>
                      </div>
                      <div className='detail-value'>
                        <span>{charge?.inclusive ? 'Inclusive' : 'Exclusive'}</span>
                      </div>
                    </Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* New Pricing Model Breakdown Structure */}
            {record?.pricing_model_breakdown && record?.pricing_model_breakdown?.length > 0 && (
              <div className='details-section'>
                <div className='heading-section'>
                  <h3>Pricing Breakdown</h3>
                </div>
                {record?.pricing_model_breakdown?.map((pricingModel, idx) => (
                  <PricingModelBreakdown key={pricingModel?.pricing_model_id || idx} pricingModel={pricingModel} index={idx} currency={record?.currency} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Modal trigger='click' centered open={open} width={window.innerWidth < 768 ? 350 : 600} footer={null} destroyOnClose onCancel={handleOpen}>
        <div className='mobileVersion'>
          <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
            <span className='createIcon'>
              <ScheduleOutlined />
            </span>
          </div>
          <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
            {type === 'payment_link' ? 'Send Payment Link To Customer' : 'Email Invoice PDF To Customer'}
          </div>
          <div className='radio-button' style={{ marginBottom: '12px' }}>
            <Radio.Group name='radiogroup' onChange={handleRadioChange} value={mode}>
              <Radio style={{ fontWeight: '400' }} value={'invoice_customer'}>
                Invoice Customer
              </Radio>
              <Radio style={{ fontWeight: '400' }} value={'customer'}>
                Customer
              </Radio>
            </Radio.Group>
          </div>
          <div className='formDetails'>
            <Form form={form} layout='vertical' onFinish={handleSubmit} initialValues={initialValues}>
              {mode === 'customer' && (
                <div className='form-inputs'>
                  <Form.Item
                    name='email'
                    label='Email'
                    className='customInput mobileInputView'
                    rules={[
                      {
                        required: true,
                        message: 'Email is required',
                      },
                      {
                        type: 'email',
                        message: 'Please enter a valid email address',
                      },
                    ]}>
                    <Input placeholder='Enter Email' name='email' className='input-placeholder' suffix={<MailOutlined style={{ color: '#7F7D83' }} />} />
                  </Form.Item>
                  <Form.Item
                    name='name'
                    label='Name'
                    className='customInput mobileInputView'
                    rules={[
                      {
                        required: true,
                        message: 'Name is required',
                      },
                      {
                        type: 'string',
                        message: 'Please enter a valid name',
                      },
                    ]}>
                    <Input placeholder='Enter Name' name='name' className='input-placeholder' suffix={<UserOutlined style={{ color: '#7F7D83' }} />} />
                  </Form.Item>
                  <Form.Item
                    name='phone'
                    label='Phone'
                    className='customInput mobileInputView'
                    rules={[
                      {
                        required: true,
                        message: 'Phone number is required',
                      },
                      {
                        type: 'string',
                        message: 'Please enter a valid phone number',
                      },
                    ]}>
                    <Input placeholder='Enter Phone Number' name='phone' className='input-placeholder' suffix={<PhoneOutlined style={{ color: '#7F7D83' }} />} />
                  </Form.Item>
                </div>
              )}
              <div className='mb-[-20px] form_button update-button'>
                <Form.Item>
                  <div style={{ display: 'flex', justifyContent: 'right' }}>
                    <ANTButton
                      block
                      className='rounded-lg'
                      onClick={() => {
                        handleOpen();
                        setMode('invoice_customer');
                        setType(null);
                      }}>
                      Cancel
                    </ANTButton>
                    <Button block className='button-theme ml-4 rounded-lg' htmlType='submit' loading={isPaymentLinkSending || isInvoicePdfSending}>
                      Send
                    </Button>
                  </div>
                </Form.Item>
              </div>
            </Form>
          </div>
        </div>
      </Modal>

      <Modal trigger='click' open={openNote} className='refund-modal' width={window?.innerWidth < 768 ? 300 : 400} footer={null} destroyOnClose onCancel={handleOpenNoteModal}>
        <div className='mobileVersion'>
          <div className='font-semibold text-base'>Note For Manual Payment</div>
          <div className='mb-6'>
            <div>
              <p className='font-medium mb-1' style={{ margin: '12px 0px 3px 0px' }}>
                Custom Note
              </p>
              <Input type='text' style={{ height: '40px' }} onChange={(e) => setManualPaidNote(e?.target?.value)} placeholder={'Enter Custom Note'} />
            </div>
            <div>
              <p className='font-medium mb-1' style={{ margin: '12px 0px 3px 0px' }}>
                Attachment <span style={{ color: '#8C8C8C', fontWeight: 400 }}>(optional)</span>
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ANTButton icon={<UploadOutlined />} onClick={handleAttachmentClick}>
                  {attachmentFile ? attachmentFile?.name : 'Select File'}
                </ANTButton>
              </div>
              <input type='file' ref={hiddenFileInput} onChange={handleAttachmentFileChange} style={{ display: 'none' }} />
            </div>
            {attachmentFile && (
              <span onClick={handleAttachmentRemove} style={{ color: 'red', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px' }}>
                Remove
              </span>
            )}
          </div>
        </div>
        <div className='flex mt-4 modalButton'>
          <Button
            onClick={() => {
              setOpenNote(false);
              setAttachmentFile(null);
            }}
            block
            className='rounded-lg bg-plain'>
            Cancel
          </Button>
          <Button className='ml-4 primaryBtn rounded-lg' disabled={manualPaidNote ? false : true} block onClick={handleInvoicePaid} loading={isInvoicePaid}>
            Confirm
          </Button>
        </div>
      </Modal>

      {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN, USER_ROLES.XPAY_SUBSCRIPTION_USER]) && (
        <div className={`flex items-center gap-4 ${isLoading || record ? 'footer footer-action-button' : ''}`}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }} className='subscription-button'>
              <Skeleton.Input active style={{ marginTop: '0' }} className='first-button' />
              <Skeleton.Input active style={{ marginTop: '0' }} className='second-button' />
            </div>
          ) : (
            record && (
              <>
                {record?.state !== 'paid' && (
                  <div>
                    <Button
                      className='primaryBtn table-action-btn flex items-center justify-center'
                      block
                      onClick={() => {
                        setOpenNote(true);
                      }}
                      loading={isInvoicePaid}>
                      Mark as Paid
                    </Button>
                  </div>
                )}

                {record?.state !== 'paid' && (
                  <div>
                    <Button
                      className='primaryBtn table-action-btn flex items-center justify-center'
                      block
                      onClick={() => {
                        setType('payment_link');
                        handleOpen();
                      }}
                      loading={isPaymentLinkSending}>
                      Send Payment Link
                    </Button>
                  </div>
                )}

                {record?.pricing_model_breakdown && record?.pricing_model_breakdown?.length > 0 && (
                  <div>
                    <Button
                      className='primaryBtn table-action-btn flex items-center justify-center'
                      block
                      onClick={() => {
                        setType('invoice_pdf');
                        handleOpen();
                      }}
                      loading={isInvoicePdfSending}>
                      Email Invoice PDF
                    </Button>
                  </div>
                )}
              </>
            )
          )}
        </div>
      )}
    </>
  );
};
